import { translate } from '../../../common/i18n';
import { getUUID, recoverFromError, doUntilDone } from '../tools';
import { contractStatus, info, notify } from '../broadcast';
import { purchaseSuccessful } from './state/actions';
import { BEFORE_PURCHASE } from './state/constants';
import GTM from '../../../common/gtm';
import config from '../../common/const';

let delayIndex = 0;
let purchaseReference;

export default Engine =>
    class Purchase extends Engine {
        purchaseSingle = async params => {
            const {
                contractType,
                duration,
                durationUnit,
                barrier,
                barrier2,
                amount,
                growthRate,
                multiplier,
                limitOrder,
            } = params;


            const { currency, symbol } = this.tradeOption;
            const template = {
                amount       : Number(amount).toFixed(config.lists.CRYPTO_CURRENCIES.includes(currency) ? 8 : 2),
                basis        : 'stake',
                contract_type: contractType,
                currency,
                duration,
                duration_unit: durationUnit,
                symbol,
            };

            if (barrier !== undefined) {
                template.barrier = barrier;
            }

            if (barrier2 !== undefined) {
                template.barrier2 = barrier2;
            }

            if (growthRate !== undefined) {
                template.growth_rate = growthRate;
                delete template.duration;
                delete template.duration_unit;
            }

            if (multiplier !== undefined) {
                template.multiplier = multiplier;
                template.limit_order = limitOrder;
                delete template.duration;
                delete template.duration_unit;
            }

            const onSuccess = response => {
                const { buy } = response;
                const isVirtual = this.virtualSettings.active && this.virtualSettings.valid && this.virtualSettings.ongoing;
                const data = isVirtual ? this.dataVirtual : this.data;
                GTM.pushDataLayer({ event: 'bot_purchase', buy_price: amount });
                buy.transaction_id = config.debug && !isVirtual
                    ? Number(config.transactionId.toString() + buy.transaction_id.toString().slice(-8, -2) + 1)
                    : buy.transaction_id;

                contractStatus({
                    id      : 'contract.purchase_recieved',
                    data    : buy.transaction_id,
                    proposal: undefined,
                    currency,
                });
                data.contract = { debugId: buy.transaction_id };
                this.subscribeToOpenContract(buy.contract_id);
                this.store.dispatch(purchaseSuccessful());

                delayIndex = 0;
                notify('info', `${translate('Bought')}: ${buy.longcode} (${translate('ID')}: ${buy.transaction_id})`);

                info({
                    accountID      : this.accountInfo.loginid,
                    totalRuns      : this.updateAndReturnTotalRuns(),
                    transaction_ids: { buy: buy.transaction_id },
                    contract_type  : contractType,
                    buy_price      : buy.buy_price,
                });
            };
            this.isSold = false;
            contractStatus({
                id      : 'contract.purchase_sent',
                data    : amount,
                proposal: { ask_price: template.amount },
                currency,
            });

            const action = () => {
                const api =
                    this.virtualSettings.active && this.virtualSettings.valid && this.virtualSettings.ongoing
                        ? this.virtualApi
                        : this.api;

                if (
                    !!this.data.copyTradingTokens &&
                    this.data.copyTradingTokens.length > 0 &&
                    (!this.virtualSettings.active || !this.virtualSettings.valid || !this.virtualSettings.ongoing)
                ) {
                    const obj = {
                        buy_contract_for_multiple_accounts: '1',
                        price                             : template.amount,
                        tokens                            : this.data.copyTradingTokens,
                        parameters                        : JSON.parse(JSON.stringify(template)),
                    };
                    delete obj.parameters.passthrough;
                    api.socket.send(JSON.stringify(obj));
                }
                const obj = JSON.parse(JSON.stringify(template));
                delete obj.passthrough;
                return api.buyContractParams(obj, 100000);
            };
            // if (!this.options.timeMachineEnabled) {
            //     return doUntilDone(action).then(onSuccess);
            // }

            // try {
            //     await this.api.getPriceProposalForContract(template);
            // } catch (error) {
            //     return this.$scope.observer.emit('Error', error);
            // }

            return recoverFromError(
                action,
                (errorCode, makeDelay) => {
                    // if disconnected no need to resubscription (handled by live-api)
                    if (errorCode !== 'DisconnectError') {
                        this.renewProposalsOnPurchase();
                    } else {
                        this.clearProposals();
                    }

                    const unsubscribe = this.store.subscribe(() => {
                        const { scope, proposalsReady } = this.store.getState();
                        if (scope === BEFORE_PURCHASE && proposalsReady) {
                            makeDelay().then(() => this.observer.emit('REVERT', 'before'));
                            unsubscribe();
                        }
                    });
                },
                ['PriceMoved', 'InvalidContractProposal'],
                delayIndex++
            ).then(onSuccess);
        };
        purchase = async (contractType) => {
            // Prevent calling purchase twice
            if (this.store.getState().scope !== BEFORE_PURCHASE) {
                return Promise.resolve();
            }
            // console.log(this);
            const template = this.proposalTemplates.find(a => a.contract_type === contractType);
            const { currency, proposal } = this.selectProposal(contractType);
            const onSuccess = response => {
                // Don't unnecessarily send a forget request for a purchased contract.
                const isVirtual = this.virtualSettings.active && this.virtualSettings.valid && this.virtualSettings.ongoing;
                const data = isVirtual ? this.dataVirtual : this.data;
                data.proposals = data.proposals.filter(p => p.id !== response.echo_req.buy);
                const { buy } = response;
                GTM.pushDataLayer({ event: 'bot_purchase', buy_price: proposal.ask_price });
                buy.transaction_id = config.debug && !isVirtual
                    ? Number(config.transactionId.toString() + buy.transaction_id.toString().slice(-8, -2) + 1)
                    : buy.transaction_id;
                contractStatus({
                    id  : 'contract.purchase_recieved',
                    data: buy.transaction_id,
                    proposal,
                    currency,
                });
                data.contract = { debugId: buy.transaction_id };
                this.subscribeToOpenContract(buy.contract_id);
                this.store.dispatch(purchaseSuccessful());
                // this.renewProposalsOnPurchase();
                this.onTrade = true;
                delayIndex = 0;
                notify('info', `${translate('Bought')}: ${buy.longcode} (${translate('ID')}: ${buy.transaction_id})`);

                info({
                    accountID      : this.accountInfo.loginid,
                    totalRuns      : this.updateAndReturnTotalRuns(),
                    transaction_ids: { buy: buy.transaction_id },
                    contract_type  : contractType,
                    buy_price      : buy.buy_price,
                });
            };

            this.isSold = false;

            contractStatus({
                id  : 'contract.purchase_sent',
                data: proposal.ask_price,
                proposal,
                currency,
            });

            const action = () => {
                const api =
                    this.virtualSettings.active && this.virtualSettings.valid && this.virtualSettings.ongoing
                        ? this.virtualApi
                        : this.api;
                const copyTokens = this.getCopyTradingTokens || [];

                if (
                    copyTokens.length > 0 &&
                    (!this.virtualSettings.active || !this.virtualSettings.valid || !this.virtualSettings.ongoing)
                ) {
                    const obj = {
                        buy_contract_for_multiple_accounts: '1',
                        price                             : proposal.ask_price,
                        tokens                            : copyTokens,
                        parameters                        : JSON.parse(JSON.stringify(template)),
                    };
                    // this.api.events.on('*', r => {
                    //     console.log(r);
                    // })
                    delete obj.parameters.passthrough;
                    // console.log(JSON.stringify(obj));
                    api.socket.send(JSON.stringify(obj));
                }
                const obj = JSON.parse(JSON.stringify(template));
                delete obj.passthrough;

                return api.buyContractParams(obj, 100000);
                // return api.buyContract(proposal.id, proposal.ask_price);
            };
            if (!this.options.timeMachineEnabled) {
                return doUntilDone(action).then(onSuccess);
            }

            return recoverFromError(
                action,
                (errorCode, makeDelay) => {
                    // if disconnected no need to resubscription (handled by live-api)
                    if (errorCode !== 'DisconnectError') {
                        this.renewProposalsOnPurchase();
                    } else {
                        this.clearProposals();
                    }

                    const unsubscribe = this.store.subscribe(() => {
                        const { scope, proposalsReady } = this.store.getState();
                        if (scope === BEFORE_PURCHASE && proposalsReady) {
                            makeDelay().then(() => this.observer.emit('REVERT', 'before'));
                            unsubscribe();
                        }
                    });
                },
                ['PriceMoved', 'InvalidContractProposal'],
                delayIndex++
            ).then(onSuccess);
        }
        getPurchaseReference = () => purchaseReference;
        regeneratePurchaseReference = () => {
            purchaseReference = getUUID();
        };
    };



// WEBPACK FOOTER //
// ./src/botPage/bot/TradeEngine/Purchase.js